"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Settings, Loader2, CheckCircle } from "lucide-react"

export function ClaimListingButton({
  slug,
  isLoggedIn,
  existingStatus,
}: {
  slug: string
  isLoggedIn: boolean
  existingStatus: "none" | "pending" | "approved" | "rejected"
}) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState("")
  const [verificationEmail, setVerificationEmail] = useState("")
  const [evidenceUrl, setEvidenceUrl] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(existingStatus === "pending")
  const [error, setError] = useState("")

  if (existingStatus === "approved") return null

  if (!isLoggedIn) {
    return (
      <a
        href={`/login?callbackUrl=/residency/${slug}`}
        className="inline-flex items-center gap-1.5 text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
      >
        <Settings size={12} />
        Manage this listing?
      </a>
    )
  }

  if (submitted || existingStatus === "pending") {
    return (
      <div className="flex items-center gap-1.5 text-xs text-amber-700">
        <CheckCircle size={12} />
        Claim request pending review
      </div>
    )
  }

  if (existingStatus === "rejected") {
    return (
      <div className="text-xs text-[var(--muted-foreground)]">
        Claim request was not approved
      </div>
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError("")
    try {
      const res = await fetch(`/api/residencies/${slug}/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, verificationEmail: verificationEmail || undefined, evidenceUrl: evidenceUrl || undefined }),
      })
      if (res.ok) {
        setSubmitted(true)
        setOpen(false)
        router.refresh()
      } else {
        const data = await res.json().catch(() => null)
        setError(typeof data?.error === "string" ? data.error : "Something went wrong")
      }
    } catch {
      setError("Network error")
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors cursor-pointer"
      >
        <Settings size={12} />
        Manage this listing?
      </button>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="border border-[var(--border)] rounded-lg p-4 space-y-3 mt-3">
      <p className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">
        Claim this listing
      </p>
      <p className="text-xs text-[var(--muted-foreground)]">
        Tell us about your connection to this residency. Claims are reviewed by our team.
      </p>
      <div>
        <label className="text-xs font-medium block mb-1">Your role / affiliation *</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. I'm the program director at..."
          required
          minLength={10}
          rows={3}
          className="w-full border border-[var(--border)] rounded-[var(--radius)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
        />
      </div>
      <div>
        <label className="text-xs font-medium block mb-1">Link to staff page or LinkedIn (optional)</label>
        <Input
          type="url"
          value={evidenceUrl}
          onChange={(e) => setEvidenceUrl(e.target.value)}
          placeholder="e.g. https://residencyname.org/staff or LinkedIn profile"
        />
        <p className="text-[11px] text-[var(--muted-foreground)] mt-1">
          A link showing you on the organization&apos;s team speeds up approval.
        </p>
      </div>
      <div>
        <label className="text-xs font-medium block mb-1">Email at this organization (optional)</label>
        <Input
          type="email"
          value={verificationEmail}
          onChange={(e) => setVerificationEmail(e.target.value)}
          placeholder="you@residencyname.org"
        />
        <p className="text-[11px] text-[var(--muted-foreground)] mt-1">
          Providing a matching domain email also helps.
        </p>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={submitting} className="gap-1.5">
          {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
          Submit claim
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
